/**
 * Simple Admin Loader Component
 * Clean, professional loader for admin interface without cultural elements
 */

import React from 'react';

export interface SimpleAdminLoaderProps {
  size?: 'small' | 'medium' | 'large';
  message?: string;
  className?: string;
}

export const SimpleAdminLoader: React.FC<SimpleAdminLoaderProps> = ({
  size = 'medium',
  message,
  className = ''
}) => {
  const sizeClasses = {
    small: 'w-4 h-4',
    medium: 'w-6 h-6',
    large: 'w-8 h-8'
  };

  const borderClasses = {
    small: 'border-2',
    medium: 'border-3',
    large: 'border-4'
  };

  return (
    <div
      className={`flex items-center justify-center space-x-3 ${className}`}
      role="status"
      aria-live="polite"
    >
      <div className={`${sizeClasses[size]} relative`}>
        {/* Simple spinning circle */}
        <div className={`w-full h-full rounded-full ${borderClasses[size]} border-gray-200 border-t-blue-600 animate-spin`}></div>
      </div>
      {message && (
        <p className="text-sm text-gray-600">{message}</p>
      )}
    </div>
  );
};

// Full-screen overlay loader for admin interface
export interface SimpleAdminOverlayLoaderProps {
  isVisible: boolean;
  message?: string;
  onCancel?: () => void;
  /** Tailwind stacking class for the overlay root (default z-50) */
  stackClassName?: string;
}

export const SimpleAdminOverlayLoader: React.FC<SimpleAdminOverlayLoaderProps> = ({
  isVisible,
  message = 'Loading...',
  onCancel,
  stackClassName = 'z-50'
}) => {
  const [shouldRender, setShouldRender] = React.useState(isVisible);
  const [isAnimating, setIsAnimating] = React.useState(isVisible);

  React.useEffect(() => {
    if (isVisible) {
      setShouldRender(true);
      // Small delay to trigger animation
      const animationTimeout = setTimeout(() => setIsAnimating(true), 10);
      return () => clearTimeout(animationTimeout);
    } else {
      setIsAnimating(false);
      // Wait for animation to complete before unmounting
      const unmountTimeout = setTimeout(() => setShouldRender(false), 200);
      return () => clearTimeout(unmountTimeout);
    }
  }, [isVisible]);

  if (!shouldRender) return null;

  return (
    <div
      className={`fixed inset-0 ${stackClassName} flex items-center justify-center transition-opacity duration-300 ${
        isAnimating ? 'opacity-100' : 'opacity-0'
      }`}
      style={{ pointerEvents: isVisible ? 'auto' : 'none' }}
    >
      {/* Clean background overlay */}
      <div className="absolute inset-0 bg-gray-900/50 backdrop-blur-sm"></div>

      {/* Loading container with professional design */}
      <div
        className={`relative bg-white rounded-lg p-6 shadow-xl border border-gray-200 mx-4 transform transition-all duration-300 ${
          isAnimating ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
        }`}
        style={{
          width: 'fit-content',
          minWidth: '320px'
        }}
      >
        <div className="flex items-center justify-center space-x-3">
          {/* Simple spinner */}
          <div className="w-8 h-8 border-4 border-gray-200 border-t-blue-600 rounded-full animate-spin" aria-hidden="true"></div>

          {/* Message */}
          {message && (
            <div className="text-gray-700 font-medium">
              {message}
            </div>
          )}
        </div>

        {/* Cancel button if provided */}
        {onCancel && (
          <div className="mt-4 text-center">
            <button
              onClick={onCancel}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors duration-200 rounded border border-gray-300 hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SimpleAdminLoader;

// Centered page-level loader that matches the admin login experience
export const AdminPageLoader: React.FC<{ message?: string }> = ({ message = 'Loading...' }) => {
  return <SimpleAdminOverlayLoader isVisible message={message} />;
};